import { Flex } from 'antd';
import React, { useContext, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';

import { StepProps } from '@/widgets/ServiceFormTemplate';

import { ConditionView, Informer, StepControlPanel, Loader } from '@/shared/ui';
import { predefinedInformers } from '@/shared/ui/Informer/model/predefinedInformers';
import { PredefinedInformerType } from '@/shared/ui/Informer/types';

import { config } from '@/config/index';

import { ServiceFileManager } from '@/helpers/serviceFileManager';

import AuthContext from '@/contexts/Auth/AuthContext';

import {
  asBase64String,
  IFRAME_CONTAINER,
  lib,
  previousSign,
  signAlgo,
  signType,
  WIDGET_ID,
  WIDGET_URI,
  external,
} from './lib/constants';

import './sigm-up.scss';

export interface SignUpProps {
  serviceFileManager: ServiceFileManager;
  submitForm: (formData: any) => Promise<void>;
  beforeSignupSubmit?: (specificStep: StepProps['specificStep']) => Promise<boolean | void>;
}

export const SignUp = React.memo<StepProps<SignUpProps>>(function SignUp({
  serviceFileManager,
  submitForm,
  next,
  prev,
  beforeSignupSubmit,
  specificStep,
}) {
  const [errorText, setErrorText] = useState<string>('');
  const [isFormInSubmitProcess, setIsFormInSubmitProcess] = useState<boolean>(false);
  const [isSigned, setIsSigned] = useState<boolean>(config.signUp.testFlow);
  const [isSecondQrActive, setIsSecondQrActive] = useState<boolean>(false);
  const isPrivateKeyReadRef = useRef(false);
  const { getValues } = useFormContext();
  const { userData } = useContext(AuthContext);

  useEffect(() => {
    const widget = new lib.EndUser(IFRAME_CONTAINER, WIDGET_ID, WIDGET_URI, lib.EndUser.FormType.ReadPKey, null);

    widget.iframe.onload = async (): Promise<void> => {
      try {
        isPrivateKeyReadRef.current = false;
        setErrorText('');

        await widget.AddEventListener(lib.EndUser.EventType.ConfirmKSPOperation, () => {
          if (isPrivateKeyReadRef.current) {
            setIsSecondQrActive(true);
          }
        });

        const privateKey = await widget.ReadPrivateKey();
        isPrivateKeyReadRef.current = true;

        const signUpPayload = await serviceFileManager.getUint8Array();
        if (!signUpPayload) {
          setErrorText('Не вдалося згенерувати файл для підпису');
          setIsSecondQrActive(false);
          return;
        }

        if (!config.signUp.testFlow && userData?.drfo_code !== privateKey?.[0]?.['infoEx']?.['subjDRFOCode']) {
          setErrorText('РНОКПП не збігається з вказаним у профілі');
          setIsSecondQrActive(false);
          return;
        }

        const signed = await widget.SignData(signUpPayload, external, asBase64String, signAlgo, previousSign, signType);

        if (signed) {
          serviceFileManager.setSignature(signed);
          setIsSigned(true);
          setIsSecondQrActive(false);
        }
      } catch (error) {
        console.error(error);
        setErrorText('Не вдалося виконати підпис через id.gov.ua.');
        setIsSecondQrActive(false);
      }
    };

    return () => {
      widget.m_promises = [];
    };
  }, [serviceFileManager, userData]);

  const qrDiiaNextCfg = predefinedInformers[PredefinedInformerType.QRCODE_DIIA_NEXT] ?? {};

  const handleNextStep = async (): Promise<void> => {
    setIsFormInSubmitProcess(true);

    try {
      const shouldContinue = beforeSignupSubmit ? await beforeSignupSubmit(specificStep) : true;

      if (shouldContinue === false) {
        return;
      }

      await submitForm(getValues());
      next();
    } catch (error) {
      console.error(error);
    } finally {
      setIsFormInSubmitProcess(false);
    }
  };

  return (
    <ConditionView if={!isFormInSubmitProcess} else={<Loader />}>
      <Flex vertical style={{ padding: '24px', height: '100%' }}>
        <Flex vertical>
          <div id="sign-widget-parent" className="row"></div>
        </Flex>
        <ConditionView if={!!errorText}>
          <div>
            <div className="text-error">Виникла помилка:</div>
            <div className="text-error">{errorText}</div>
          </div>
        </ConditionView>
        <ConditionView if={isSecondQrActive}>
          <Informer
            borderColor={qrDiiaNextCfg.borderColor}
            icon={qrDiiaNextCfg.icon}
            header={
              qrDiiaNextCfg.header?.text
                ? { text: qrDiiaNextCfg.header.text, color: qrDiiaNextCfg.header.color }
                : undefined
            }
            text={
              qrDiiaNextCfg.text?.text ? { text: qrDiiaNextCfg.text.text, color: qrDiiaNextCfg.text.color } : undefined
            }
          />
        </ConditionView>
      </Flex>

      <StepControlPanel disabledNext={!isSigned} prev={prev} next={handleNextStep} />
    </ConditionView>
  );
});
